import React, { useState, useEffect } from 'react';
import { useParams, useLocation, Link } from 'react-router-dom';
import Header from '../../components/Header/Header';
import Footer from '../../components/Footer/Footer';
import Breadcrumbs from '../../components/common/Breadcrumbs/Breadcrumbs';
import './ActivityPage.css';
import { activityService } from '../../services/activityService';
import { aboutService } from '../../services/aboutService';
import { ReactComponent as ArrowRight } from '../../assets/icons/arrow-right.svg';
import { ReactComponent as ArrowLeft } from '../../assets/icons/arrow-left.svg';

const ActivityPage = () => {
  const { activityId } = useParams();
  const location = useLocation();
  const [data, setData] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [currentImageIndex, setCurrentImageIndex] = useState(0);

  useEffect(() => {
    const fetchData = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const isAboutPage = location.pathname === '/about' || activityId === 'about';
        
        if (isAboutPage) {
          const response = await aboutService.getAbout();
          setData({
            ...response.about,
            type: 'about'
          });
        } else {
          const slug = activityId || location.pathname.substring(1);
          const response = await activityService.getActivityBySlug(slug);
          setData({
            ...response.activity,
            type: 'activity'
          });
        }
      } catch (error) {
        console.error('Ошибка загрузки данных:', error);
        setError('Не удалось загрузить данные');
      } finally {
        setIsLoading(false);
      }
    };

    fetchData();
  }, [activityId, location.pathname]);

  useEffect(() => {
    setCurrentImageIndex(0);
  }, [data]);

  const getBreadcrumbsItems = () => {
    if (data?.type === 'about') {
      return [
        { path: '/', label: 'Главная' },
        { path: '/about', label: 'О парке' }
      ];
    }
    
    return [
      { path: '/', label: 'Главная' },
      { path: '/', label: 'Развлечения' },
      { path: `/activity/${data?.slug}`, label: data?.title || 'Загрузка...' }
    ];
  };

  const getTableType = () => {
    if (!data?.prices?.length) return 'none';
    const hasDescription = data.prices.some(item => item.description);
    return hasDescription ? 'three-columns' : 'two-columns';
  };

  const nextImage = () => {
    if (data?.images && currentImageIndex < data.images.length - 1) {
      setCurrentImageIndex(prev => prev + 1);
    }
  };

  const prevImage = () => {
    if (currentImageIndex > 0) {
      setCurrentImageIndex(prev => prev - 1);
    }
  };

  if (isLoading) {
    return (
      <div className="activity-page-wrapper">
        <Header />
        <main className="activity-page-main">
          <div className="activity-page-loader-container">
            <div className="activity-page-loader"></div>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  if (error || !data) {
    return (
      <div className="activity-page-wrapper">
        <Header />
        <main className="activity-page-main">
          <div className="activity-page-error-container">
            <h2 className="activity-page-error-title">Страница не найдена</h2>
            <p className="activity-page-error-text">{error || 'Запрошенная страница не существует'}</p>
            <Link to="/" className="back-home-button">
              <button className="back-to-news-btn">
                <span>Вернуться на главную</span>
              </button>
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const tableType = getTableType();
  const breadcrumbsItems = getBreadcrumbsItems();
  const hasGallery = data.images && data.images.length > 0;

  return (
    <div className="activity-page-wrapper">
      <Header />
      
      <main className="activity-page-main">
        <div className="activity-page-breadcrumbs-section">
          <Breadcrumbs items={breadcrumbsItems} />
        </div>
        
        <div className="activity-page-container">
          <article className="activity-page-article">
            
            <div className="activity-page-image-container">
              <img
                src={data.coverImage || data.image}
                alt={data.title}
                className="activity-page-image"
              />
            </div>
            
            <h1 className="activity-page-title">{data.title}</h1>
            
            <div className="activity-page-description">
              {(data.fullDescription || data.description || '').split('\n').map((paragraph, index) => {
                const trimmed = paragraph.trim();
                
                if (trimmed.startsWith('•') || trimmed.startsWith('-')) {
                  return (
                    <div key={index} className="activity-page-list-wrapper">
                      <ul className="activity-page-list">
                        <li className="activity-page-list-item">
                          {trimmed.substring(1).trim()}
                        </li>
                      </ul>
                    </div>
                  );
                }
                
                if (trimmed === '') {
                  return <div key={index} className="activity-page-empty-line"></div>;
                }
                
                return (
                  <p key={index} className="activity-page-paragraph">
                    {paragraph}
                  </p>
                );
              })}
            </div>
            
            {data.hasPrice && data.prices && data.prices.length > 0 && (
              <div className="activity-page-price-section">
                <h2 className="activity-page-price-title">Прайс-лист</h2>
                
                {tableType === 'two-columns' && (
                  <table className="activity-page-price-table two-columns">
                    <thead>
                      <tr>
                        <th className="price-header service-header">Услуга</th>
                        <th className="price-header price-header-right">Цена</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.prices.map((item, index) => (
                        <tr key={index}>
                          <td className="price-cell service-cell">{item.service}</td>
                          <td className="price-cell price-cell-right">{item.price}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
                
                {tableType === 'three-columns' && (
                  <table className="activity-page-price-table three-columns">
                    <thead>
                      <tr>
                        <th className="price-header service-header">Услуга</th>
                        <th className="price-header description-header">Описание</th>
                        <th className="price-header price-header-right">Цена</th>
                      </tr>
                    </thead>
                    <tbody>
                      {data.prices.map((item, index) => (
                        <tr key={index}>
                          <td className="price-cell service-cell">{item.service}</td>
                          <td className="price-cell description-cell">{item.description || ''}</td>
                          <td className="price-cell price-cell-right">{item.price}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                )}
              </div>
            )}
            
            {hasGallery && (
              <div className="activity-page-gallery-section">
                <h2 className="activity-page-gallery-title">Галерея</h2>
                
                <div className="gallery-carousel-container">
                  <button
                    className={`gallery-nav-arrow gallery-prev ${currentImageIndex === 0 ? 'disabled' : ''}`}
                    onClick={prevImage}
                    disabled={currentImageIndex === 0}
                  >
                    <ArrowLeft />
                  </button>

                  <div className="gallery-main-image">
                    <img
                      src={data.images[currentImageIndex].imageUrl}
                      alt={`${data.title} - ${currentImageIndex + 1}`}
                      className="gallery-main-img"
                    />
                    {data.images[currentImageIndex].caption && (
                      <div className="gallery-caption">
                        {data.images[currentImageIndex].caption}
                      </div>
                    )}
                  </div>

                  <button
                    className={`gallery-nav-arrow gallery-next ${currentImageIndex >= data.images.length - 1 ? 'disabled' : ''}`}
                    onClick={nextImage}
                    disabled={currentImageIndex >= data.images.length - 1}
                  >
                    <ArrowRight />
                  </button>
                </div>

                <div className="gallery-indicators">
                  {data.images.map((_, index) => (
                    <button
                      key={index}
                      className={`gallery-indicator ${index === currentImageIndex ? 'active' : ''}`}
                      onClick={() => setCurrentImageIndex(index)}
                    />
                  ))}
                </div>
              </div>
            )}
            
          </article>
        </div>
      </main>
      
      <Footer />
    </div>
  );
};

export default ActivityPage;